import { useEffect, useState } from 'react';

import type { Hex } from '@/store/ids';
import type { WorkerClient } from '@/transport/workerClient';

/**
 * The two things the conversation header shows beyond the title: the photo, and
 * whether the other person is around.
 *
 * Both are deliberately per-conversation rather than per-list. A media grant
 * lasts sixty seconds, so showing photos down the whole chat list would mean one
 * grant request per row per minute — a request storm in proportion to how many
 * conversations somebody has, for decoration. The list draws generated initials
 * until there is a longer-lived or batched avatar grant to draw on, which is a
 * server change rather than something the client can paper over.
 */

/** A grant is refreshed slightly before it expires, not after it has failed. */
const GRANT_REFRESH_MARGIN_MS = 10_000;

/**
 * Resolves the conversation photo to a URL, re-requesting it as the grant nears
 * expiry.
 *
 * Returns null while there is no photo, no worker, or no grant yet — which the
 * avatar renders as initials. A null is never a failure state the reader needs to
 * see: a missing picture is not an error.
 */
export function useConversationPhoto(
  worker: WorkerClient | null,
  conversationId: Hex | null,
  photoMediaId: Hex | null,
): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!worker || conversationId === null || photoMediaId === null) {
      setUrl(null);
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const fetchGrant = () => {
      void worker
        .call('get-download-grant', {
          conversationId,
          // A conversation photo hangs off the conversation, not off a message.
          messageId: null,
          mediaId: photoMediaId,
          variant: 'thumb',
          purpose: 'conversation-photo',
        })
        .then((grant) => {
          if (cancelled) return;
          setUrl(grant.url);
          // Scheduled from the server's own expiry rather than from a fixed
          // interval, so a changed grant lifetime needs no client change.
          const delay = Math.max(5_000, grant.expiresAt - Date.now() - GRANT_REFRESH_MARGIN_MS);
          timer = setTimeout(fetchGrant, delay);
        })
        .catch(() => {
          // No photo is shown and nothing is said. The conversation is readable
          // either way, and a broken-image placeholder would be worse than
          // initials.
          if (!cancelled) setUrl(null);
        });
    };

    fetchGrant();
    return () => {
      cancelled = true;
      if (timer !== null) clearTimeout(timer);
    };
  }, [worker, conversationId, photoMediaId]);

  return url;
}

/** How often presence is refreshed while a conversation is open. */
const PRESENCE_REFRESH_MS = 45_000;

export interface PresenceState {
  online: boolean;
  /** Minute-truncated by the server, or null when unknown or not visible. */
  lastSeenAt: number | null;
}

/**
 * Presence for the other party in a direct conversation.
 *
 * Polled rather than pushed, which is the server's design: pushing presence to
 * every contact of every account is a fan-out proportional to the social graph
 * for a value only looked at while a conversation is open.
 *
 * An absent or hidden account and an offline one are the same answer by design,
 * so this reports "not online, no time" for both and the header simply shows
 * nothing. It must not say "hidden" — the server deliberately does not tell it
 * that, and inventing the distinction here would leak what the server withheld.
 */
export function usePresence(
  worker: WorkerClient | null,
  userId: Hex | null,
): PresenceState | null {
  const [state, setState] = useState<PresenceState | null>(null);

  useEffect(() => {
    if (!worker || userId === null) {
      setState(null);
      return;
    }

    let cancelled = false;

    const poll = () => {
      void worker
        .call('get-presence', { userIds: [userId] })
        .then((response) => {
          if (cancelled) return;
          const entry = response.entries.find((candidate) => candidate.userId === userId);
          setState(
            entry === undefined
              ? null
              : { online: entry.online, lastSeenAt: entry.lastSeenAt },
          );
        })
        .catch(() => {
          // Presence is decoration. A failure leaves the subtitle blank rather
          // than putting an error where somebody's status goes.
          if (!cancelled) setState(null);
        });
    };

    poll();
    const timer = setInterval(poll, PRESENCE_REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [worker, userId]);

  return state;
}
